import {
  type Content,
  type ContentHref,
  type ContentRelation,
  ContentType,
} from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import {
  selectApiWithPendingChangesRelationsToAndFromMap,
  selectBaselineRelationsToAndFromMap,
} from '@newStore/documentApi/documentApiSelectors';
import type { RelationsToAndFromMap } from '@newStore/documentApi/documentApiTypes';
import {
  selectExternalContent,
  selectExternalContentRelationsMap,
} from '@newStore/externalData/externalDataSelectors';
import { config as documentTypes } from '@store/constants/documentTypes';
import { createTypedSelector, mapRelationsToRelationsToAndFrom } from '@newStore/genericHelpers';
import { getExternalGoalOrItem } from '@newStore/llinkid/llinkidSelectors';
import type { EditAsideGoalRelations } from '@nodeTypeConfig/configTypes';
import { getGoalIdentifier } from '@newStore/llinkid/llinkidHelpers';
import { getPathToRoot } from '@newStore/externalData/externalDataHelpers';
import type { ReferenceFrame } from '@newStore/referenceFrames/referenceFramesState';
import { getAllDescendantParentChildHrefs } from '@newStore/documentUI/documentUIHelpers';

const selectLlinkidOdet = (state: RootState): ReferenceFrame => {
  return state.referenceFrames?.[`/content/${documentTypes.llinkidOdetCurriculumKey}`];
};

export const isLlinkidOdetLoading = (state: RootState) => {
  return !selectLlinkidOdet(state)?.isLoaded;
};

export const selectLlinkidOdetCurriculum = createTypedSelector(
  [(state: RootState) => selectLlinkidOdet(state)],
  (llinkidOdet) => {
    return Object.values(llinkidOdet.content).find(
      (r) => r.type === ContentType.CURRICULUM_ODET,
    ) as Content;
  },
);

export const selectLlinkidOdetCurriculumGoals = createTypedSelector(
  [(state: RootState) => selectLlinkidOdet(state)],
  (llinkidOdet) => {
    return Object.values(llinkidOdet.content).filter(
      (r) => r.type === ContentType.CURRICULUM_ODET_DEVELOPMENT_GOAL,
    );
  },
);

const getDisplayOption = (item, rootOdet): string => {
  if (item.identifiers?.length) {
    const goalIdentifier = getGoalIdentifier(
      getPathToRoot(
        mapRelationsToRelationsToAndFrom(rootOdet.relations),
        rootOdet.content,
        item.$$meta.permalink,
      ),
    );
    return `${goalIdentifier} - ${item.title}`;
  }
  return item.title;
};

export const selectFormattedOdetChildren = createTypedSelector(
  [
    (state) => selectLlinkidOdet(state),
    (state, selectedValues) => selectedValues,
  ],
  (rootOdet, alreadySelectedGoals) => {
    if (!rootOdet) {
      return [];
    }

    const alreadySelectedKeys = new Set(alreadySelectedGoals);

    const relationsMap = mapRelationsToRelationsToAndFrom(rootOdet.relations);
    return getAllDescendantParentChildHrefs(relationsMap.to, rootOdet.href)
      .filter((href) => !alreadySelectedKeys.has(href.childHref.split('/').pop()))
      .flatMap(
        (hrefRecord) => {
          const node = rootOdet.content[hrefRecord.childHref];
          if (![
            'CURRICULUM_ODET_KEY_COMPETENCE',
            'CURRICULUM_ODET_DEVELOPMENT_GOAL',
            'CURRICULUM_ODET_ENDTERM',
          ].includes(node.type)) {
            return [];
          }
          return {
            key: node.key,
            $$meta: {
              permalink: node.$$meta.permalink,
            },
            displayName: getDisplayOption(node, rootOdet),
          };
        },
      );
  },
);

const getGoalRelations = (
  config: EditAsideGoalRelations,
  currentEditingHref: ContentHref,
  externalContent: Record<ContentHref, Content>,
  relationsMap: RelationsToAndFromMap,
) => {
  const relationOrigin = !config.options.revertedRelationDirection ? 'from' : 'to';
  const relationEnd = !config.options.revertedRelationDirection ? 'to' : 'from';
  const isRelationTypeRelationIncluded = config.options.relationTypes.includes('RELATION');

  const allRelations = relationsMap[relationOrigin][currentEditingHref];

  const endRelations = relationsMap[relationEnd][currentEditingHref];

  const filterFn = (relation: ContentRelation) => {
    return (
      config.options.relationTypes.includes(relation.relationtype) &&
      externalContent[relation[relationEnd].href] &&
      (!config.options.toType ||
        config.options.toType === externalContent[relation[relationEnd].href].type)
    );
  };

  let relations = allRelations?.filter(filterFn);

  // special case of RELATION relations: get to relations and reverse
  if (isRelationTypeRelationIncluded) {
    const toRelations = endRelations?.filter(
      (r) => r.relationtype === 'RELATION' && externalContent[r[relationOrigin].href],
    );

    if (toRelations) {
      const reversedToRelations = toRelations.map((r) => ({
        ...r,
        from: r.to,
        to: r.from,
        $$reversed: true,
      })) as ContentRelation[];

      relations = relations.concat(reversedToRelations.filter(filterFn));
    }
  }
  return relations;
};

export const selectAsideGoalRelations = createTypedSelector(
  [
    (state: RootState) => selectExternalContent(state),
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
    (state) => state.documentUI.currentEditingNode as ContentHref,
    (state, href: ContentHref, config: EditAsideGoalRelations) => config,
  ],
  (externalContent, relationsMap, currentEditingHref, config) => {
    return getGoalRelations(config, currentEditingHref, externalContent, relationsMap);
  },
);

export const selectChangeMessageForGoalRelations = createTypedSelector(
  [
    (state) => selectExternalContent(state),
    (state) => selectExternalContentRelationsMap(state),
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
    (state) => selectBaselineRelationsToAndFromMap(state),
    (state, href: ContentHref) => href,
    (state, href: ContentHref, config: EditAsideGoalRelations) => config,
  ],
  (externalContent, externalRelationsMap, relationsMap, rawRelationsMap, href, config) => {
    const relationEnd = !config.options.revertedRelationDirection ? 'to' : 'from';

    const newContentHrefs: ContentHref[] = getGoalRelations(
      config,
      href,
      externalContent,
      relationsMap,
    )?.map((rel) => rel[relationEnd].href as ContentHref);

    const oldContentHrefs: ContentHref[] = getGoalRelations(
      config,
      href,
      externalContent,
      rawRelationsMap,
    )?.map((rel) => rel[relationEnd].href as ContentHref);

    const oldValuesMap = new Map(
      oldContentHrefs?.map((oHref) => [
        oHref,
        getExternalGoalOrItem(externalRelationsMap, externalContent, oHref),
      ]),
    );

    const newValuesMap = new Map(
      newContentHrefs?.map((nHref) => [
        nHref,
        getExternalGoalOrItem(externalRelationsMap, externalContent, nHref),
      ]),
    );

    const allKeys = new Set([...oldValuesMap.keys(), ...newValuesMap.keys()]);

    const changes = [...allKeys]
      .map((key) => {
        const oldGoal = oldValuesMap.get(key);
        const newGoal = newValuesMap.get(key);

        if (oldGoal && !newGoal) {
          return `<del>${oldGoal.identifier ? `${oldGoal.identifier} - ` : ''}${oldGoal.descriptionText
            } (v${oldGoal.version})</del>`;
        }
        if (newGoal && !oldGoal) {
          return `<ins>${newGoal.identifier ? `${newGoal.identifier} - ` : ''}${newGoal.descriptionText
            } (v${newGoal.version})</ins>`;
        }
        return null;
      })
      .filter((change) => change !== null);

    if (changes.length === 0) {
      return null;
    }

    return `<ul>${changes.map((change) => `<li>${change}</li>`).join('')}</ul>`;
  },
);
